/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useEffect } from 'react';
import cfrService from '../../../../services/cfr.service';
import { Box, Button, CircularProgress } from '@mui/material';
import LLMChatDialog , {ChatMessage} from '../../../common/LLMChatDialog';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import LlmService from '../../../../services/llm.service';
import { CommentRole } from '../../../../@types/llm-conversation';

function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

export default function CFRLookUpNodeModal(props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void }): ReactElement | null {
    const { open, env, onClose } = props;
    const [isCfrLoading, setIsCfrLoading] = React.useState(false);
    const [injectedMessage, setInjectedMessage] = React.useState<string | undefined>(undefined);

    // Create context to prepend to user's first message
    const contextPrepend = React.useMemo(() => {
        if (!env || env.selection.length === 0) {
            return '';
        }
        
        const node = env!.selection[0].get('data');
        if (!node) {
            return '';
        }

        const nodeName = node.name || 'Unknown Node';
        const nodeDescription = node.description || '';
        const statementOfTruth = node.statementOfTruth || '';
        // Build the context with separated groups
        let context = `Context: Fault Tree Node Analysis

Node Name: ${nodeName}
${nodeDescription ? `Description: ${nodeDescription}\n` : ''}${statementOfTruth ? `Statement of Truth: "${statementOfTruth}"\n` : ''}
`;


        context += `
---

User Question: `;

        return context;
    }, [env]);

    
    const handleCfrLookup = (): void => {
        // Initialize or fetch data needed for CFR Lookup when modal opens
        const [node] = env!.selection;
        const name = node.get('data').name;
        const statementOfTrue = node.get('data').statementOfTruth;
        const description = node.get('data').description;

        setIsCfrLoading(true);

        // Step 1: Show "Querying Daedalus..." status
        const statusMessage: ChatMessage = {
            role: 'system',
            content: 'Querying Daedalus...'
        };
        // eslint-disable-next-line no-console
        console.log('Message to inject:', JSON.stringify({ type: 'status', message: statusMessage }));
        setInjectedMessage(JSON.stringify({ type: 'status', message: statusMessage }));

        // Step 2: Query Daedalus for CFR regulations
        cfrService.searchForNodeRegulations(name, statementOfTrue, description)
            .then((results) => {
                // Handle results (e.g., set state to display them)
                // Format the results for the LLM
                const formattedResults = cfrService.formatResultsForContext(results);

                // Step 3: Update status to "Querying Daedalus...done. Summarizing regulatory evaluations..."
                const summarizingStatus: ChatMessage = {
                    role: 'system',
                    content: 'Querying Daedalus...done. Summarizing regulatory evaluations...'
                };
                setInjectedMessage(JSON.stringify({ type: 'status', message: summarizingStatus }));


                // Create the question to inject into the chat
                const question = `CFR REGULATION ANALYSIS REQUEST

Barrier/Precursor: "${name || 'Unknown'}"
${statementOfTrue ? `Statement of Truth: "${statementOfTrue}"` : ''}

RELEVANT CFR REGULATIONS FOUND:
${formattedResults}

Please provide a REGULATION-FOCUSED analysis that:

1. **Regulatory Summary**: For each relevant CFR section found, cite the specific section number (e.g., "14 CFR §91.123") and explain what it requires or prohibits.

2. **Applicability to This Barrier**: Explain exactly how each regulation applies to the barrier "${name || 'Unknown'}" - cite specific regulatory language where possible.

3. **Compliance Considerations**: Based on the mapping justifications above, identify any patterns that suggest regulatory compliance issues or violations. Reference specific CFR sections.

4. **Key Regulatory Requirements**: Provide a bullet-point list of the most important regulatory requirements (with CFR citations) that operators/pilots must follow related to this barrier.

Be specific with regulatory citations (chapter and verse). Format CFR references consistently as "14 CFR §XX.XXX".`;


                LlmService.postLlmConversation({
                    eventSequenceId: 0,
                    includeESD: false,
                    comments: [{
                        role: CommentRole.USER,
                        content: question,
                        timestamp: new Date().toISOString()
                    }],
                    queryTime: new Date().toISOString(),
                    error: null as any
                }).then((response) => {
                    // eslint-disable-next-line no-console
                    console.log('LLM Response:', response);
                    // Step 5: Extract the summary and inject as final assistant message
                    const responseComments = response.data?.comments || [];
                    const lastComment = responseComments[responseComments.length - 1];
                    if (lastComment?.content) {
                        // Inject the final summary as an assistant message (visible to user)
                        const finalStatus: ChatMessage = {
                            role: 'system',
                            content: 'Querying Daedalus...done. Summarizing regulatory evaluations...done.'
                        };
                        const assistantResponse: ChatMessage = {
                            role: 'assistant',
                            content: lastComment.content
                        };
                        setInjectedMessage(JSON.stringify({
                            type: 'cfr_result',
                            status: finalStatus,
                            result: assistantResponse
                        }));
                    } else {
                        throw new Error('No summary generated');
                    }
                });
            })
            .catch((error) => {
                const errorStatus: ChatMessage = {
                    role: 'system',
                    content: `CFR lookup failed: ${error.message || 'Unknown error'}`
                };
                setInjectedMessage(JSON.stringify({ type: 'status', message: errorStatus }));
            })
            .finally(() => {
                setIsCfrLoading(false);
            });
    };

    // useEffect(() => {
    //     if (open) {
    //         handleCfrLookup();
    //     }
    // }, [open]);

    // CFR Lookup button for header
    const cfrLookupButton = (
        <Button
            size="small"
            variant="outlined"
            onClick={handleCfrLookup}
            disabled={isCfrLoading}
            sx={{ ml: 1, fontSize: '0.7rem', py: 0.25, minHeight: 0 }}
            startIcon={isCfrLoading ? <CircularProgress size={12} /> : undefined}
        >
            {isCfrLoading ? 'Searching...' : 'CFR Lookup'}
        </Button>
    );

    // Wrapper styles for flex height propagation
    const wrapperSx = {
        flex: 1,
        minHeight: 450,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                CFR for Node {env?.selection[0]?.get('data')?.uniqueId || 'Unknown'}
            </DialogTitle>
            <DialogContent dividers>
                <LLMChatDialog
                    title="Click this button to query CFR regulations"
                    contextPrepend={contextPrepend}
                    contextMessage="No AI justifications available for analysis. Reports may be manually mapped or lack justification text."
                    minHeight={450}
                    maxHeight={600}
                    headerActions={cfrLookupButton}
                    onClose={onClose}
                    fillHeight={false}
                    injectedMessage={injectedMessage}
                    onInjectedMessageProcessed={() : void => setInjectedMessage(undefined)}
                />
            </DialogContent>
        </Dialog>
    );
}